/**
 * Get common props for Select component
 * @param {import('./propTypes').SelectProps} props
 */
import { components as selectComponents } from 'react-select';

import cs from 'classnames';

import { isKioskTouchMode } from 'lib/hooks/useKioskTouchMode';

import Icon from '../../Icon/Icon';
import ActionButton from '../../shared/ActionButton';
import { SortSelectValues, SortValueLabel } from '../components/OrderSelectedOptions';

import { customStyle } from './style';

const TOUCH_SEARCHABLE_MIN_OPTIONS = 15;
const TOUCH_MAX_MENU_HEIGHT = 220;

// A menu portaled to <body> measures free space against the whole viewport, so it opens
// downward into the area the wizard/modal footer covers and its last options become
// unreachable. Rendering in flow lets react-select's auto placement flip it above the
// field when the scroll container has no room below.
export const getTouchMenuProps = () =>
  isKioskTouchMode()
    ? {
        menuPortalTarget: undefined,
        maxMenuHeight: TOUCH_MAX_MENU_HEIGHT,
        menuShouldScrollIntoView: false
      }
    : {};

// react-select renders the chosen value as plain text (not a form field), so Datadog session
// replay's mask-user-input level doesn't cover it — mask the control (value + search input).
export const MaskedControl = (props) => (
  <selectComponents.Control
    {...props}
    innerProps={{ ...props.innerProps, 'data-dd-privacy': 'mask' }}
  />
);

// iOS Safari ignores autocomplete="off" and offers saved logins on bare text inputs;
// an unrecognized token is the only reliable way to suppress the credential bubble.
const NoAutofillInput = (props) => <selectComponents.Input {...props} autoComplete="none" />;

export const formatImageOption = ({ image, value, label, imageClassName, disabled }) => {
  return (
    <div className="flex items-center gap-x-2">
      {image ? (
        <div
          className={cs(
            'rounded-smooth flex h-6 w-6 items-center justify-center overflow-hidden bg-neutral-100',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          )}>
          <img
            className={cs(imageClassName ? imageClassName : 'h-full w-full object-cover')}
            src={image}
            alt={value}
          />
        </div>
      ) : (
        <Icon
          icon="no-image-square"
          className={disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
          size={24}
        />
      )}
      {label}
    </div>
  );
};

export const getCommonSelectProps = ({
  // Basic props
  className,
  id,
  name,
  label,
  placeholder = 'Select',
  value = null,
  options = [],
  onChange,
  inputValue,
  defaultOptions = [],

  // Select behavior
  isMulti = false,
  isClearable = true,
  isSearchable,
  isAsync,
  disabled = false,
  isLoading,
  noDropdown,

  // Styling
  styles,
  width,
  minWidth,
  height,
  error,

  // Icons and layout
  icon,
  headerIcon,
  rightIcon,
  rightText,

  // Formatting
  formatOptionLabel,
  formatCreateLabel,
  formatMultiValueContainer,
  filterOption,

  // Action button
  actionButtonText,
  actionButtonIcon,
  actionButtonClick,

  // Other
  menuPortalTarget,
  forwardedRef,
  hasOrder,
  components: componentsOverride,

  ...rest
}) => {
  const hasActionButton = actionButtonText || actionButtonIcon;

  return {
    className: cs('select', className),
    classNamePrefix: 'select',
    id: id || name || label ? `select-${id || name || label}` : 'selectId',
    placeholder,
    value,
    options,
    onChange,
    isMulti,
    isClearable,
    isDisabled: disabled,
    isLoading,
    inputValue,
    defaultOptions,
    headerIcon,
    styles: customStyle({
      width,
      minWidth,
      height,
      disabled,
      error,
      icon,
      isMulti,
      rightIcon,
      hasOrder,
      styles
    }),
    inputId: id,
    components: {
      Control: MaskedControl,
      Input: NoAutofillInput,
      IndicatorSeparator: () => rightText,
      ...(noDropdown && {
        DropdownIndicator: () => <span className="w-2 opacity-0">none</span>
      }),
      ...(formatMultiValueContainer && {
        MultiValueContainer: formatMultiValueContainer
      }),
      ...(hasActionButton && {
        DropdownIndicator: () => (
          <ActionButton
            text={actionButtonText}
            icon={actionButtonIcon}
            onClick={actionButtonClick}
          />
        )
      }),
      ...(hasOrder &&
        isMulti && {
          ValueContainer: SortSelectValues,
          MultiValueLabel: SortValueLabel
        }),
      ...componentsOverride
    },
    formatOptionLabel,
    formatCreateLabel,
    filterOption,
    menuPortalTarget,
    menuPlacement: 'auto',
    // keep touch scroll inside the menu instead of bubbling to the page (kiosk)
    captureMenuScroll: true,
    isSearchable:
      isSearchable ??
      (isAsync
        ? undefined
        : !isKioskTouchMode() || (options?.length ?? 0) > TOUCH_SEARCHABLE_MIN_OPTIONS),
    ref: forwardedRef,
    ...rest
  };
};
